import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { toast } from "sonner";
import { PaperPlaneRight, ArrowRight, Package, Warning, Paperclip, X, DownloadSimple } from "@phosphor-icons/react";
import { portalApi } from "@/lib/portalApi";
import { formatApiError } from "@/lib/api";
import { BRAND } from "./portalBrand";

const MODES = ["Sea-FCL", "Sea-LCL", "Air", "Road"];
const INCOTERMS = ["", "EXW", "FOB", "CIF", "CFR", "DAP", "DDP", "FCA"];
const IMO_CLASSES = ["", "1", "2.1", "2.2", "2.3", "3", "4.1", "4.2", "4.3", "5.1", "5.2", "6.1", "6.2", "7", "8", "9"];
const PACKING_GROUPS = ["", "I", "II", "III"];
const EMPTY_HAZ = { un_number: "", imo_class: "", proper_shipping_name: "", packing_group: "", flash_point_c: "", marine_pollutant: false, net_weight_kg: "", emergency_contact: "" };
const EMPTY = { job_type: "Export", mode: "Sea-FCL", pol_name: "", pod_name: "", commodity: "", incoterms: "", cargo_type: "", equipment: "", quantity: "", weight_kg: "", volume_cbm: "", ready_date: "", shipper_name: "", consignee_name: "", notes: "", is_hazardous: false, haz: { ...EMPTY_HAZ } };

// Customer-facing state is gated on portal_published, not the internal job status:
// a booking accepted internally still shows "Under review" until staff share the copy.
const bookingState = (b) => {
  if (/declined/i.test(b.status || "")) return { label: "Declined", chip: "text-rose-700 border-rose-200 bg-rose-50", confirmed: false };
  if (b.portal_published) return { label: "Confirmed", chip: "text-emerald-700 border-emerald-200 bg-emerald-50", confirmed: true };
  return { label: "Under review", chip: "text-amber-700 border-amber-200 bg-amber-50", confirmed: false };
};

const lbl = "text-[11px] font-semibold uppercase tracking-wider text-slate-500";
const fld = "mt-1 w-full h-10 px-3 bg-slate-50 border border-slate-300 rounded-md text-sm text-slate-900 focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100";

export default function PortalBookings() {
  const location = useLocation();
  const [form, setForm] = useState({ ...EMPTY, ...(location.state?.prefill || {}) });
  const [hazFiles, setHazFiles] = useState([]);
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setHaz = (k, v) => setForm((f) => ({ ...f, haz: { ...f.haz, [k]: v } }));

  const load = () => portalApi.get("/portal/bookings").then(({ data }) => setRows(data || [])).catch(() => {}).finally(() => setLoading(false));
  useEffect(() => { load(); }, []);

  const onHazFiles = async (e) => {
    const files = Array.from(e.target.files || []);
    const read = (file) => new Promise((res) => {
      const r = new FileReader();
      r.onload = () => res({ name: file.name, file_data: r.result, file_type: file.type });
      r.readAsDataURL(file);
    });
    const out = await Promise.all(files.map(read));
    setHazFiles((prev) => [...prev, ...out].slice(0, 6));
    e.target.value = "";
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.pol_name.trim() || !form.pod_name.trim()) { toast.error("Origin and destination are required"); return; }
    if (form.is_hazardous) {
      const h = form.haz || {};
      if (!h.un_number?.trim() || !h.imo_class || !h.proper_shipping_name?.trim()) {
        toast.error("For DG cargo, enter UN number, IMO class and proper shipping name");
        return;
      }
    }
    setBusy(true);
    try {
      const payload = {
        ...form,
        weight_kg: form.weight_kg ? Number(form.weight_kg) : null,
        volume_cbm: form.volume_cbm ? Number(form.volume_cbm) : null,
        haz: form.is_hazardous ? form.haz : null,
        haz_documents: form.is_hazardous ? hazFiles : [],
      };
      const { data } = await portalApi.post("/portal/bookings", payload);
      toast.success(`Booking ${data.booking_no} submitted — we'll confirm it shortly.`);
      setForm({ ...EMPTY, haz: { ...EMPTY_HAZ } });
      setHazFiles([]);
      load();
    } catch (err) { toast.error(formatApiError(err)); } finally { setBusy(false); }
  };

  const downloadCopy = async (b) => {
    try {
      const { data } = await portalApi.get(`/portal/bookings/${b.id}/booking-copy`);
      const a = document.createElement("a");
      a.href = data.file_data.startsWith("data:") ? data.file_data : `data:${data.file_type || "application/octet-stream"};base64,${data.file_data}`;
      a.download = data.file_name || `booking-copy-${b.booking_no || b.id}`;
      document.body.appendChild(a); a.click(); a.remove();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  return (
    <div>
      <div className="mb-5">
        <h1 className="text-2xl font-bold text-slate-900">Book a Shipment</h1>
        <p className="text-slate-500 text-sm mt-1">Place a booking with {BRAND.name}. Our team confirms it and it becomes a live shipment.</p>
      </div>

      <form onSubmit={submit} className="bg-white border border-slate-200 rounded-xl shadow-sm p-5 space-y-4">
        <div className="flex flex-wrap gap-3">
          <div className="inline-flex bg-slate-100 rounded-lg p-1">
            {["Export", "Import"].map((t) => (
              <button type="button" key={t} onClick={() => set("job_type", t)} className={`px-4 py-1.5 text-sm rounded-md ${form.job_type === t ? "bg-blue-700 text-white" : "text-slate-600"}`}>{t}</button>
            ))}
          </div>
          <div className="inline-flex bg-slate-100 rounded-lg p-1">
            {MODES.map((m) => (
              <button type="button" key={m} onClick={() => set("mode", m)} className={`px-3 py-1.5 text-sm rounded-md ${form.mode === m ? "bg-blue-700 text-white" : "text-slate-600"}`}>{m}</button>
            ))}
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          <div><label className={lbl}>Origin (POL) *</label><input value={form.pol_name} onChange={(e) => set("pol_name", e.target.value)} className={fld} placeholder="e.g. Nhava Sheva" /></div>
          <div><label className={lbl}>Destination (POD) *</label><input value={form.pod_name} onChange={(e) => set("pod_name", e.target.value)} className={fld} placeholder="e.g. New York" /></div>
          <div><label className={lbl}>Cargo ready date</label><input type="date" value={form.ready_date} onChange={(e) => set("ready_date", e.target.value)} className={fld} /></div>
          <div><label className={lbl}>Commodity</label><input value={form.commodity} onChange={(e) => set("commodity", e.target.value)} className={fld} placeholder="What are you shipping?" /></div>
          <div><label className={lbl}>Cargo type</label><input value={form.cargo_type} onChange={(e) => set("cargo_type", e.target.value)} className={fld} placeholder="General / DG / Reefer…" /></div>
          <div><label className={lbl}>Incoterms</label><select value={form.incoterms} onChange={(e) => set("incoterms", e.target.value)} className={fld}>{INCOTERMS.map((i) => <option key={i} value={i}>{i || "—"}</option>)}</select></div>
          <div><label className={lbl}>Equipment</label><input value={form.equipment} onChange={(e) => set("equipment", e.target.value)} className={fld} placeholder="20GP / 40HC / LCL…" /></div>
          <div><label className={lbl}>Quantity</label><input value={form.quantity} onChange={(e) => set("quantity", e.target.value)} className={fld} placeholder="e.g. 2 x 40HC" /></div>
          <div className="grid grid-cols-2 gap-2">
            <div><label className={lbl}>Weight (kg)</label><input type="number" value={form.weight_kg} onChange={(e) => set("weight_kg", e.target.value)} className={fld} /></div>
            <div><label className={lbl}>Volume (cbm)</label><input type="number" value={form.volume_cbm} onChange={(e) => set("volume_cbm", e.target.value)} className={fld} /></div>
          </div>
          <div><label className={lbl}>Shipper</label><input value={form.shipper_name} onChange={(e) => set("shipper_name", e.target.value)} className={fld} placeholder="Shipper name" /></div>
          <div><label className={lbl}>Consignee</label><input value={form.consignee_name} onChange={(e) => set("consignee_name", e.target.value)} className={fld} placeholder="Consignee name" /></div>
        </div>
        <div><label className={lbl}>Notes</label><textarea value={form.notes} onChange={(e) => set("notes", e.target.value)} rows={2} className={fld + " h-auto py-2"} placeholder="Anything else we should know?" /></div>

        {/* Hazardous / DG cargo */}
        <div className="rounded-lg border border-amber-200 bg-amber-50/60 p-4">
          <label className="flex items-center gap-2.5 cursor-pointer select-none">
            <input type="checkbox" checked={form.is_hazardous} onChange={(e) => set("is_hazardous", e.target.checked)} className="h-4 w-4 accent-amber-600" data-testid="booking-haz-toggle" />
            <span className="inline-flex items-center gap-1.5 text-sm font-semibold text-amber-800"><Warning size={16} weight="fill" /> This is hazardous / dangerous goods (DG) cargo</span>
          </label>

          {form.is_hazardous && (
            <div className="mt-4 space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                <div><label className={lbl}>UN Number *</label><input value={form.haz.un_number} onChange={(e) => setHaz("un_number", e.target.value)} className={fld} placeholder="e.g. UN1263" data-testid="haz-un" /></div>
                <div><label className={lbl}>IMO / IMDG Class *</label><select value={form.haz.imo_class} onChange={(e) => setHaz("imo_class", e.target.value)} className={fld} data-testid="haz-class">{IMO_CLASSES.map((c) => <option key={c} value={c}>{c || "—"}</option>)}</select></div>
                <div><label className={lbl}>Packing Group</label><select value={form.haz.packing_group} onChange={(e) => setHaz("packing_group", e.target.value)} className={fld}>{PACKING_GROUPS.map((p) => <option key={p} value={p}>{p || "—"}</option>)}</select></div>
                <div className="sm:col-span-2 lg:col-span-3"><label className={lbl}>Proper Shipping Name *</label><input value={form.haz.proper_shipping_name} onChange={(e) => setHaz("proper_shipping_name", e.target.value)} className={fld} placeholder="As per IMDG / MSDS" data-testid="haz-psn" /></div>
                <div><label className={lbl}>Flash Point (°C)</label><input value={form.haz.flash_point_c} onChange={(e) => setHaz("flash_point_c", e.target.value)} className={fld} placeholder="e.g. 23" /></div>
                <div><label className={lbl}>Net Weight (kg)</label><input value={form.haz.net_weight_kg} onChange={(e) => setHaz("net_weight_kg", e.target.value)} className={fld} placeholder="DG net weight" /></div>
                <div><label className={lbl}>24h Emergency Contact</label><input value={form.haz.emergency_contact} onChange={(e) => setHaz("emergency_contact", e.target.value)} className={fld} placeholder="Name & phone" /></div>
                <div className="flex items-center gap-2 pt-6">
                  <input id="haz-mp" type="checkbox" checked={!!form.haz.marine_pollutant} onChange={(e) => setHaz("marine_pollutant", e.target.checked)} className="h-4 w-4 accent-amber-600" />
                  <label htmlFor="haz-mp" className="text-sm text-slate-700">Marine pollutant</label>
                </div>
              </div>

              <div>
                <label className={lbl}>DG Documents (MSDS / DG declaration)</label>
                <div className="mt-1.5 flex items-center gap-3 flex-wrap">
                  <label className="inline-flex items-center gap-2 h-10 px-3 rounded-md border border-amber-300 bg-white text-amber-800 text-sm cursor-pointer hover:bg-amber-50">
                    <Paperclip size={15} /> Attach files
                    <input type="file" multiple accept=".pdf,.jpg,.jpeg,.png,.doc,.docx" className="hidden" onChange={onHazFiles} data-testid="haz-files" />
                  </label>
                  <span className="text-[11px] text-slate-500">PDF / image / Word · up to 6 files (~8 MB each)</span>
                </div>
                {hazFiles.length > 0 && (
                  <div className="mt-2 flex flex-wrap gap-2">
                    {hazFiles.map((f, i) => (
                      <span key={i} className="inline-flex items-center gap-1.5 text-xs bg-white border border-slate-200 rounded-full pl-2.5 pr-1.5 py-1 text-slate-700">
                        <Paperclip size={12} className="text-slate-400" /> <span className="max-w-[180px] truncate">{f.name}</span>
                        <button type="button" onClick={() => setHazFiles((prev) => prev.filter((_, x) => x !== i))} className="text-slate-400 hover:text-rose-600"><X size={12} /></button>
                      </span>
                    ))}
                  </div>
                )}
              </div>
              <p className="text-[11px] text-amber-700/80">Please ensure the DG declaration and MSDS match the details above. Our team reviews all DG bookings before confirming.</p>
            </div>
          )}
        </div>

        <div className="flex justify-end">
          <button type="submit" disabled={busy} className="inline-flex items-center gap-2 h-11 px-5 rounded-md bg-blue-700 hover:bg-blue-800 text-white text-sm font-semibold disabled:opacity-50">
            <PaperPlaneRight size={16} /> {busy ? "Submitting…" : "Submit booking"}
          </button>
        </div>
      </form>

      <h2 className="text-sm font-semibold text-slate-700 mt-8 mb-3">My bookings</h2>
      {loading ? <div className="text-slate-400 text-sm">Loading…</div> : rows.length === 0 ? (
        <div className="bg-white border border-slate-200 rounded-xl py-10 text-center text-slate-500 text-sm">No bookings yet. Submit one above.</div>
      ) : (
        <div className="space-y-2">
          {rows.map((b) => {
            const st = bookingState(b);
            const hasCopy = st.confirmed && b.booking_copy?.document_id;
            return (
              <div key={b.id} className="bg-white border border-slate-200 rounded-xl p-4">
                <div className="flex items-center justify-between gap-3 flex-wrap">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-mono font-semibold text-slate-800 text-sm">{b.job_number || b.booking_no}</span>
                    <span className="text-[10px] font-semibold uppercase tracking-wider rounded-full px-2 py-0.5 border border-slate-200 bg-slate-50 text-slate-600">{b.job_type} · {b.mode}</span>
                    {b.hazardous && <span className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider rounded-full px-2 py-0.5 border border-amber-200 bg-amber-50 text-amber-700"><Warning size={11} weight="fill" /> DG {b.haz_details?.imo_class ? `· ${b.haz_details.imo_class}` : ""}</span>}
                    <span className="text-sm text-slate-700">{b.pol_name || "—"} <ArrowRight size={12} className="inline -mt-0.5 text-slate-400" /> {b.pod_name || "—"}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-[11px] text-slate-400">{(b.created_at || "").slice(0, 10)}</span>
                    <span className={`text-[10px] font-semibold uppercase tracking-wider rounded-full px-2 py-0.5 border ${st.chip}`}>{st.label}</span>
                    {st.confirmed && <Package size={15} className="text-blue-600" />}
                  </div>
                </div>
                {b.status === "Booking Declined" && b.booking_declined_reason && <div className="text-xs text-rose-600 mt-2">Reason: {b.booking_declined_reason}</div>}
                {b.description_of_goods && <div className="text-xs text-slate-500 mt-1">{b.description_of_goods}{b.incoterms ? ` · ${b.incoterms}` : ""}</div>}
                {!st.confirmed && !/declined/i.test(b.status || "") && (
                  <div className="text-[11px] text-amber-700 mt-1.5">Our team is reviewing your booking. You'll be able to download the booking copy here once it's confirmed.</div>
                )}
                {st.confirmed && (
                  <div className="flex items-center gap-3 mt-3 pt-3 border-t border-slate-100">
                    {hasCopy && (
                      <button onClick={() => downloadCopy(b)} data-testid={`booking-download-${b.booking_no}`} className="inline-flex items-center gap-1.5 h-9 px-3 rounded-md bg-blue-700 hover:bg-blue-800 text-white text-xs font-semibold">
                        <DownloadSimple size={14} weight="bold" /> Download booking copy
                      </button>
                    )}
                    <Link to={`/portal/shipments/${b.id}`} className="inline-flex items-center gap-1 text-xs text-blue-700 font-medium hover:underline">
                      View live shipment <ArrowRight size={12} />
                    </Link>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
